import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import CustomLevelDropdown from '../ui/CustomLevelDropdown';
import CustomUnitDropdown from '../ui/CustomUnitDropdown';
import { AddServiceProvider } from '../forms/AddEditServiceProvider';
import { createServiceProvider } from '../../services/api/implementation/impl';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const BulkContractorAssignmentModal = ({
  isOpen,
  onClose,
  workTypes,
  allContractors,
  tasks,
  onAssignContractors,
  isSubmitting = false,
  getUserColor,
  preSelectedWorkType = null,
  showAllTasksInitially = true,
  setIsSubmitting,
  levels = [],
  units = [],
  refetchContractors = () => {} // Add refetch function prop
}) => {
  // Local state for the modal
  const [selectedWorkType, setSelectedWorkType] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [userOptions, setUserOptions] = useState({});
  const [autoSelectedContractors, setAutoSelectedContractors] = useState([]);
  const [hasManualContractorSelection, setHasManualContractorSelection] = useState(false);

  // Add Service Provider modal state
  const [showAddSPModal, setShowAddSPModal] = useState(false);
  const [isSubmittingSP, setIsSubmittingSP] = useState(false);

  // Mutation for creating service provider
  const { mutate: handleCreateServiceProvider } = useMutation({
    mutationFn: createServiceProvider,
    onMutate: () => {
      setIsSubmittingSP(true);
    },
    onSuccess: async (data) => {
      setIsSubmittingSP(false);
      toast.success('Service Provider created successfully');
      setShowAddSPModal(false);
      // Refetch contractors to include the newly added service provider
      await refetchContractors();
    },
    onError: (err) => {
      setIsSubmittingSP(false);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error creating service provider'
      );
    }
  });

  // Handlers for Add Service Provider modal
  const handleOpenAddSPModal = () => {
    setShowAddSPModal(true);
  };

  const handleCloseAddSPModal = () => {
    setShowAddSPModal(false);
  };

  const handleSubmitServiceProvider = (payload) => {
    handleCreateServiceProvider(payload);
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedWorkType(preSelectedWorkType || '');
      setSelectedTasks([]);
      setSelectedContractors([]);
      setUserOptions({});
      setAutoSelectedContractors([]);
      setHasManualContractorSelection(false);
    }
  }, [isOpen, preSelectedWorkType]);

  // Auto-select all tasks when worktype is selected
  useEffect(() => {
    if (selectedWorkType) {
      const tasksForWorkType = getTasksByWorkType(selectedWorkType);
      const taskIds = tasksForWorkType.map((task) => task.id);
      setSelectedTasks(taskIds);
      updateCommonContractors(taskIds);
    } else {
      setSelectedTasks([]);
      setSelectedContractors([]);
      setAutoSelectedContractors([]);
    }
  }, [selectedWorkType]);

  const handleWorkTypeSelection = (workTypeId) => {
    setSelectedWorkType(workTypeId);
    // Tasks will be auto-selected in the useEffect above
    setSelectedContractors([]);
    setUserOptions({});
    setAutoSelectedContractors([]);
    setHasManualContractorSelection(false);
  };

  const getTasksByWorkType = (workTypeId) => {
    if (showAllTasksInitially && !workTypeId) {
      return tasks || [];
    }
    if (!workTypeId) return [];
    return tasks.filter((task) => task.workType_id === Number(workTypeId));
  };

  // Function to find and auto-select contractors common to all selected tasks
  const updateCommonContractors = (selectedTaskIds) => {
    if (hasManualContractorSelection) {
      return;
    }

    if (selectedTaskIds.length === 0) {
      setSelectedContractors([]);
      setAutoSelectedContractors([]);
      return;
    }

    const selectedTaskObjects = tasks.filter((task) => selectedTaskIds.includes(task.id));

    if (selectedTaskObjects.length === 0) {
      setSelectedContractors([]);
      setAutoSelectedContractors([]);
      return;
    }

    // Find contractors who are assigned to ALL selected tasks
    const commonContractorIds = selectedTaskObjects.reduce((commonContractors, task, index) => {
      const taskContractorIds = (task.spLiveTaskMapping || [])
        .filter((sp) => sp?.user?.serviceProvider?.category_id === 1)
        .map((sp) => sp.user.id);

      if (index === 0) {
        return taskContractorIds;
      } else {
        return commonContractors.filter((contractorId) => taskContractorIds.includes(contractorId));
      }
    }, []);

    setSelectedContractors(commonContractorIds);
    setAutoSelectedContractors(commonContractorIds);
  };

  const handleTaskSelection = (taskId, checked) => {
    if (checked) {
      setSelectedTasks((prev) => {
        const newSelectedTasks = [...prev, taskId];
        updateCommonContractors(newSelectedTasks);
        return newSelectedTasks;
      });
    } else {
      setSelectedTasks((prev) => {
        const newSelectedTasks = prev.filter((id) => id !== taskId);
        updateCommonContractors(newSelectedTasks);
        return newSelectedTasks;
      });
    }
  };

  const handleSelectAllTasks = (checked) => {
    if (checked) {
      const currentTasks = showAllTasksInitially
        ? getTasksByWorkType(selectedWorkType)
        : getTasksByWorkType(selectedWorkType);
      const allTaskIds = currentTasks.map((task) => task.id);
      setSelectedTasks(allTaskIds);
      updateCommonContractors(allTaskIds);
    } else {
      setSelectedTasks([]);
      if (!hasManualContractorSelection) {
        setSelectedContractors([]);
        setAutoSelectedContractors([]);
      }
    }
  };

  const handleContractorSelection = (contractorId, checked) => {
    if (checked) {
      setSelectedContractors((prev) => [...prev, contractorId]);
      // Initialize userOptions for this contractor if not exists
      setUserOptions((prev) => ({
        ...prev,
        [contractorId]: prev[contractorId] || {
          level_ids: [],
          unit_ids: [],
          externalType: false,
          levelType: false,
          unitType: false
        }
      }));
    } else {
      setSelectedContractors((prev) => prev.filter((id) => id !== contractorId));
      // Remove userOptions for this contractor
      setUserOptions((prev) => {
        const newOptions = { ...prev };
        delete newOptions[contractorId];
        return newOptions;
      });
    }

    setHasManualContractorSelection(true);
    setAutoSelectedContractors([]);
  };

  const getFormattedSelectedContractors = () => {
    return selectedContractors.reduce((acc, contractorId) => {
      const userOption = userOptions[contractorId];

      if (!userOption) return acc;

      const baseData = {
        id: Number(contractorId),
        workType_id: Number(selectedWorkType)
      };

      // Create separate objects for each true type
      if (userOption.externalType) {
        acc.push({
          ...baseData,
          levelType: 'external'
        });
      }

      if (userOption?.levelType && userOption?.level_ids?.length > 0) {
        acc.push({
          ...baseData,
          levelType: 'levelWise',
          level_ids: userOption?.level_ids?.map(Number)
        });
      }

      if (userOption.unitType && userOption?.unit_ids?.length > 0) {
        acc.push({
          ...baseData,
          levelType: 'unitWise',
          unit_ids: userOption?.unit_ids?.map(Number)
        });
      }

      return acc;
    }, []);
  };

  const handleBulkAssignment = async () => {
    // Validate selections
    if (!selectedWorkType) {
      alert('Please select a work type.');
      return;
    }

    if (selectedTasks.length === 0) {
      alert('No tasks available for the selected work type.');
      return;
    }

    if (selectedContractors.length === 0) {
      alert('Please select at least one contractor.');
      return;
    }

    // Validate contractor options (levels and units) when available
    if (levels.length > 0 || units.length > 0) {
      const workTypeName = workTypes.find((wt) => wt.id === Number(selectedWorkType));

      for (const contractorId of selectedContractors) {
        const userOption = userOptions[contractorId];

        if (!userOption) {
          alert(
            `Please configure options for ${allContractors.find((c) => c.user.id === contractorId)?.user?.name || 'selected contractor'}.`
          );
          return;
        }

        // Check if at least one option is selected (external, level, or unit)
        const hasExternalType = userOption.externalType;
        const hasLevelType =
          userOption.levelType && userOption.level_ids && userOption.level_ids.length > 0;
        const hasUnitType =
          userOption.unitType && userOption.unit_ids && userOption.unit_ids.length > 0;

        if (!hasExternalType && !hasLevelType && !hasUnitType) {
          alert(
            `Please select at least one option (External, Common Space, or Unit) for ${allContractors.find((c) => c.user.id === contractorId)?.user?.name || 'selected contractor'}.`
          );
          return;
        }

        // For specific work types, validate unit selection
        if (isWorkTypeRequiringUnits(workTypeName) && units.length > 0) {
          if (!hasUnitType && !hasExternalType) {
            alert(
              `Please select units for ${allContractors.find((c) => c.user.id === contractorId)?.user?.name || 'selected contractor'} for this work type.`
            );
            return;
          }
        }
      }
    }

    setIsSubmitting(true);

    // Get formatted contractor data
    const formattedData = getFormattedSelectedContractors();

    // Call the parent's assignment handler
    await onAssignContractors({
      selectedTasks,
      selectedContractors,
      selectedWorkType,
      formattedData
    });

    // Reset state after successful assignment
    setSelectedWorkType('');
    setSelectedTasks([]);
    setSelectedContractors([]);
    setUserOptions({});
    setAutoSelectedContractors([]);
    setHasManualContractorSelection(false);
    setIsSubmitting(false);
  };

  // Helper function to check if work type requires units
  const isWorkTypeRequiringUnits = (workTypeName) => {
    if (!workTypeName) return false;

    // Debug logging
    console.log('Work type object:', workTypeName);
    console.log('Work type name:', workTypeName.name);
    console.log('Work type label:', workTypeName.label);

    // Try different possible property names for the work type name
    const workTypeNameString = workTypeName.name || workTypeName.label || workTypeName.title || '';

    if (!workTypeNameString) {
      console.log('No work type name found, defaulting to show units');
      return true; // Default to showing units if we can't determine the work type
    }

    const workTypeNameLower = workTypeNameString.toLowerCase();

    // Check for work types that should NOT have units
    const workTypesWithoutUnits = [
      'structural work',
      'brick work',
      'plaster work',
      'structural',
      'brick',
      'plaster'
    ];

    // Check if the work type name contains any of the excluded work types
    const isExcluded = workTypesWithoutUnits.some((excludedType) =>
      workTypeNameLower.includes(excludedType)
    );

    const requiresUnits = !isExcluded;
    console.log('Requires units:', requiresUnits, 'Excluded:', isExcluded);

    return requiresUnits;
  };

  // Check if all contractor options are properly configured
  const areContractorOptionsValid = () => {
    if (levels.length === 0 && units.length === 0) return true; // No validation needed if no options available

    return selectedContractors.every((contractorId) => {
      const userOption = userOptions[contractorId];
      if (!userOption) return false;

      const hasExternalType = userOption.externalType;
      const hasLevelType =
        userOption.levelType && userOption.level_ids && userOption.level_ids.length > 0;
      const hasUnitType =
        userOption.unitType && userOption.unit_ids && userOption.unit_ids.length > 0;

      return hasExternalType || hasLevelType || hasUnitType;
    });
  };

  const handleClose = () => {
    setSelectedWorkType('');
    setSelectedTasks([]);
    setSelectedContractors([]);
    setUserOptions({});
    setAutoSelectedContractors([]);
    setHasManualContractorSelection(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isDirty={true} width={80} name="Assign Contractors to Tasks" onClose={handleClose}>
      <div className="flex flex-col gap-4">
        {/* Responsive Layout: Work Type + Tasks Left, Contractors Middle, Options Right (if available) */}
        <div
          className={`flex gap-6 ${levels.length > 0 || units.length > 0 ? '' : 'justify-between'}`}>
          {/* Left Column: Work Type and Tasks */}
          <div className="flex-1">
            {/* Work Type Selection */}
            {!preSelectedWorkType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Work Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedWorkType}
                  onChange={(e) => handleWorkTypeSelection(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                  <option value="" disabled>
                    Select Work Type
                  </option>
                  {workTypes.map((workType) => (
                    <option key={workType.id} value={workType.id}>
                      {workType.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Tasks Display */}
            {selectedWorkType && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Tasks for Selected Work Type
                  </label>
                  <span className="text-sm text-gray-500">
                    {(() => {
                      const currentTasks = getTasksByWorkType(selectedWorkType);
                      return `${currentTasks.length} task${currentTasks.length !== 1 ? 's' : ''}`;
                    })()}
                  </span>
                </div>
                <div className="border border-gray-200 rounded-md max-h-80 overflow-y-auto">
                  {(() => {
                    const currentTasks = getTasksByWorkType(selectedWorkType);

                    return currentTasks.length > 0 ? (
                      currentTasks.map((task) => (
                        <div
                          key={task.id}
                          className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 bg-gray-50">
                          <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{task.name}</div>
                            <div className="text-sm text-gray-500">Status: {task.status}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No tasks found for this work type
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Middle Column: Contractors Selection */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Contractors <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
              {allContractors.map((contractor) => {
                const isSelected = selectedContractors.includes(contractor.user.id);
                const initials = contractor.user?.name
                  .split(' ')
                  .map((word) => word[0])
                  .join('')
                  .toUpperCase();

                return (
                  <label
                    key={contractor.user.id}
                    className={`flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                      isSelected ? 'bg-green-50' : ''
                    }`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) =>
                        handleContractorSelection(contractor.user.id, e.target.checked)
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div
                      className={`w-8 h-8 rounded-full text-white flex items-center justify-center font-semibold text-sm ${getUserColor(contractor.user.id)}`}>
                      {initials}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900">{contractor.user?.name}</div>
                        {autoSelectedContractors.includes(contractor.user.id) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            pre-selected
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">Contractor</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Right Column: Contractor Options - Only show if levels or units are available */}
          {(levels.length > 0 || units.length > 0) && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Contractor Options
              </label>
              <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto p-4">
                {selectedContractors.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    Select contractors to configure their options
                  </div>
                ) : (
                  selectedContractors.map((contractorId) => {
                    const contractor = allContractors.find((c) => c.user.id === contractorId);
                    const workTypeName = workTypes.find((wt) => wt.id === Number(selectedWorkType));

                    return (
                      <div
                        key={contractorId}
                        className={`mb-6 p-4 border rounded-lg ${(() => {
                          const userOption = userOptions[contractorId];
                          if (!userOption) return 'border-gray-200';

                          const hasExternalType = userOption.externalType;
                          const hasLevelType =
                            userOption.levelType &&
                            userOption.level_ids &&
                            userOption.level_ids.length > 0;
                          const hasUnitType =
                            userOption.unitType &&
                            userOption.unit_ids &&
                            userOption.unit_ids.length > 0;

                          return hasExternalType || hasLevelType || hasUnitType
                            ? 'border-gray-200'
                            : 'border-red-300 bg-red-50';
                        })()}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div
                            className={`w-6 h-6 rounded-full text-white flex items-center justify-center font-semibold text-xs ${getUserColor(contractorId)}`}>
                            {contractor?.user?.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">
                            {contractor?.user?.name}
                          </span>
                          {(() => {
                            const userOption = userOptions[contractorId];
                            if (!userOption) return null;

                            const hasExternalType = userOption.externalType;
                            const hasLevelType =
                              userOption.levelType &&
                              userOption.level_ids &&
                              userOption.level_ids.length > 0;
                            const hasUnitType =
                              userOption.unitType &&
                              userOption.unit_ids &&
                              userOption.unit_ids.length > 0;

                            if (!hasExternalType && !hasLevelType && !hasUnitType) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Configuration Required
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        <div className="space-y-3">
                          {/* Common Space Dropdown - Only show if levels are available */}
                          <div>
                            {levels.length > 0 ? (
                              <CustomLevelDropdown
                                userId={contractorId}
                                userOptions={userOptions}
                                setUserOptions={setUserOptions}
                                levels={levels}
                              />
                            ) : (
                              <div className="text-sm text-gray-500 py-2">Loading levels...</div>
                            )}
                          </div>

                          {/* Unit Selection - Only show for certain work types and if units are available */}
                          {isWorkTypeRequiringUnits(workTypeName) && (
                            <div>
                              {units.length > 0 ? (
                                <CustomUnitDropdown
                                  userId={contractorId}
                                  userOptions={userOptions}
                                  setUserOptions={setUserOptions}
                                  units={units}
                                />
                              ) : (
                                <div className="text-sm text-gray-500 py-2">Loading units...</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={handleOpenAddSPModal}
            className="text-blue-500 hover:text-blue-700 text-sm font-semibold underline cursor-pointer">
            Add New Contractor
          </button>
          <div className="flex gap-3 justify-end">
            <Button displayType="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssignment}
              disabled={
                !selectedWorkType ||
                selectedTasks.length === 0 ||
                selectedContractors.length === 0 ||
                !areContractorOptionsValid() ||
                isSubmitting
              }>
              Assign Contractors
            </Button>
          </div>
        </div>
      </div>

      {/* Add Service Provider Modal */}
      {showAddSPModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={60}
          name="Add Service Provider"
          onClose={handleCloseAddSPModal}>
          <AddServiceProvider
            isSubmitting={isSubmittingSP}
            modalType="Add"
            onSubmit={handleSubmitServiceProvider}
            onClose={handleCloseAddSPModal}
            selectedServiceProvider={null}
            setSelectedServiceProvider={() => {}}
            setModalType={() => {}}
            setOpenAddUser={setShowAddSPModal}
            OpenAddUser={showAddSPModal}
            refetchServiceProviders={() => {}}
          />
        </Modal>
      )}
    </Modal>
  );
};

export default BulkContractorAssignmentModal;
